import React, { useEffect, useState, memo } from 'react';
import { Card, Space, DatePicker, Button, Spin, Skeleton, message, Segmented, Statistic, Row, Col, Progress, Tooltip as AntTooltip } from 'antd';
import { MetricCardHeader } from "./Shared/MetricCardHeader";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	LineChart,
	Line,
	Cell
} from 'recharts';
import {
	ClockCircleOutlined,
	ThunderboltOutlined,
	ReloadOutlined,
	DownloadOutlined,
	QuestionCircleOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getResponseTimeAnalytics } from '../../api/api';
import type { ResponseTimeAnalyticsResponse } from '../../types/api-models';
import {
	formatXAxisLabel,
	formatTooltipLabel,
	type AggregationType
} from '../../utils/dateFormatters';

const { RangePicker } = DatePicker;

interface ResponseTimeAnalyticsProps {
	isExpanded?: boolean;
	dashboardData?: any;
}

const ResponseTimeAnalytics: React.FC<ResponseTimeAnalyticsProps> = memo(({ isExpanded = false, dashboardData }) => {
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState<ResponseTimeAnalyticsResponse | null>(null);
	const [aggregation, setAggregation] = useState<AggregationType>('daily');
	const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
		dayjs().subtract(30, 'day'),
		dayjs()
	]);

	useEffect(() => {
		if (!isExpanded) {
			if (dashboardData?.response_time) {
				setData(dashboardData.response_time as ResponseTimeAnalyticsResponse);
			}
		}
	}, [isExpanded, dashboardData]);

	const fetchData = async () => {
		setLoading(true);
		try {
			const res = await getResponseTimeAnalytics(
				aggregation,
				dateRange[0]?.format('YYYY-MM-DD'),
				dateRange[1]?.format('YYYY-MM-DD')
			);
			setData(res);
		} catch (e) {
			console.error(e);
			message.error('Failed to load response time analytics');
		} finally {
			setLoading(false);
		}
	};

	const handleRefresh = () => {
		// For daily enforce a reasonable upper bound similarly to Activity Trend (90 days)
		if (aggregation === 'daily' && dateRange && dateRange[0] && dateRange[1]) {
			const diff = dateRange[1].diff(dateRange[0], 'day');
			if (diff > 90) {
				message.error('Daily view limited to 90 days');
				return;
			}
		}
		fetchData();
	};

	const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
		if (dates && dates[0] && dates[1]) {
			setDateRange([dates[0], dates[1]]);
		}
	};

	const renderCollapsedView = () => {
		if (!data) return <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>No data available</div>;
		const { 
      avgResponseTime = 0, 
      responseTimeBins = { fast: 0, medium: 0, slow: 0 }, 
      percentiles = { p50: 0, p90: 0, p95: 0 } 
    } = data;
		const total = (responseTimeBins.fast || 0) + (responseTimeBins.medium || 0) + (responseTimeBins.slow || 0);
		return (
			<div style={{ height: '100%' }}>
				<div style={{
					marginBottom: 16,
					padding: '12px 16px',
					backgroundColor: '#fafafa',
					borderRadius: '6px',
					border: '1px solid #f0f0f0'
				}}>
					<Row gutter={[16, 16]}>
						<Col span={8}>
               <MetricCardHeader 
                  title="Avg Response Time"
                  value={`${Number((avgResponseTime / 1000).toFixed(2))}s`}
                  trend={dashboardData?.response_time?.latency_trend}
                  subtitle="Time to generate response"
                  inverseTrend={true} // Up is Bad (Red)
               />
						</Col>
						<Col span={8}>
							<Statistic
								title={
									<span>
										P95 Response Time
										<AntTooltip title="95th percentile - 95% of responses are faster than this time" color="var(--text-color, #000)">
											<QuestionCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
										</AntTooltip>
									</span>
								}
								value={Number((percentiles.p95 / 1000).toFixed(2))}
								suffix="s"
								prefix={<ThunderboltOutlined />}
							/>
						</Col>
						<Col span={8}>
							<Statistic
								title={
									<span>
										Fast Responses
										<AntTooltip 
											title="Number of responses that completed in less than 2 seconds"
											color="var(--neutral-90)"
										>
											<QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
										</AntTooltip>
									</span>
								}
								value={responseTimeBins.fast || 0}
								suffix={`/ ${total}`}
							/>
						</Col>
					</Row>
				</div>

				<div style={{ height: 200 }}>
					<ResponsiveContainer width="100%" height="100%">
						<BarChart data={[
							{ name: 'Fast (<2s)', value: responseTimeBins.fast || 0, color: '#52c41a' },
							{ name: 'Medium (2-5s)', value: responseTimeBins.medium || 0, color: '#faad14' },
							{ name: 'Slow (>5s)', value: responseTimeBins.slow || 0, color: '#ff4d4f' }
						]}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="name" />
							<YAxis />
							<Tooltip formatter={(value) => [value as number, 'Responses']} />
							<Bar dataKey="value">
								{[
									{ name: 'Fast (<2s)', value: responseTimeBins.fast || 0, color: '#52c41a' },
									{ name: 'Medium (2-5s)', value: responseTimeBins.medium || 0, color: '#faad14' },
									{ name: 'Slow (>5s)', value: responseTimeBins.slow || 0, color: '#ff4d4f' }
								].map((entry, idx) => (
									<Cell key={`cell-${idx}`} fill={entry.color} />
								))}
							</Bar>
						</BarChart>
					</ResponsiveContainer>
				</div>
			</div>
		);
	};

	const renderExpandedView = () => {
		return (
			<div style={{ height: '100%' }}>
				<Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
					<Space wrap>
						<span style={{ fontSize: 13, color: '#666' }}>Aggregation:</span>
						<Segmented
							value={aggregation}
							onChange={(value) => setAggregation(value as AggregationType)}
							options={[
								{ label: 'Daily', value: 'daily' },
								{ label: 'Weekly', value: 'weekly' },
								{ label: 'Monthly', value: 'monthly' },
							]}
						/>
						{aggregation === 'daily' ? (
							<>
								<span style={{ fontSize: 13, color: '#666', marginLeft: 16 }}>Date Range:</span>
								<RangePicker
									value={dateRange}
									onChange={handleDateRangeChange}
									format="YYYY-MM-DD"
									style={{ width: 260 }}
									disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
								/>
							</>
						) : (
							<span style={{ fontSize: 13, color: '#666', marginLeft: 16, fontStyle: 'italic' }}>
								Showing last {aggregation === 'weekly' ? '52 weeks' : '12 months'}
							</span>
						)}
					</Space>
					<Space>
						<Button type="primary" onClick={handleRefresh} loading={loading}>
							Apply Filters
						</Button>
						<Button icon={<ReloadOutlined />} onClick={handleRefresh} disabled={!data}>
							Refresh
						</Button>
						<Button icon={<DownloadOutlined />} onClick={() => message.info('Export coming soon')}>
							Export
						</Button>
					</Space>
				</Space>

				{loading ? (
					<div style={{ textAlign: 'center', padding: '60px 20px' }}>
						<Spin size="large" tip="Loading response time analytics..." />
					</div>
				) : !data ? (
					<div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
						<p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view response time analytics</p>
						<p style={{ fontSize: 14 }}>Configure filters above and click "Apply Filters" to load data</p>
					</div>
				) : (
					<>
						{(() => {
              const { 
                avgResponseTime = 0, 
                responseTimeBins = { fast: 0, medium: 0, slow: 0 }, 
                percentiles = { p50: 0, p90: 0, p95: 0 }, 
                hourlyData = [], 
                trendData = [] 
              } = data || {};
							const total = (responseTimeBins.fast || 0) + (responseTimeBins.medium || 0) + (responseTimeBins.slow || 0);
							return (
								<>
									<Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
										<Col span={6}>
											<Statistic
												title={
													<span>
														Average Response Time
														<AntTooltip 
															title="Average time taken for the assistant to generate and return a response to user questions"
															color="var(--neutral-90)"
														>
															<QuestionCircleOutlined style={{ marginLeft: 4, color: '#999', fontSize: 12 }} />
														</AntTooltip>
													</span>
												}
												value={Number((avgResponseTime / 1000).toFixed(2))}
												suffix="s"
												prefix={<ClockCircleOutlined />}
												valueStyle={{ color: avgResponseTime < 2000 ? '#52c41a' : avgResponseTime < 5000 ? '#faad14' : '#ff4d4f' }}
											/>
										</Col>
										<Col span={6}>
											<Statistic title="P50 (Median)" value={Number((percentiles.p50 / 1000).toFixed(2))} suffix="s" />
										</Col>
										<Col span={6}>
											<Statistic title="P90" value={Number((percentiles.p90 / 1000).toFixed(2))} suffix="s" />
										</Col>
										<Col span={6}>
											<Statistic title="P95" value={Number((percentiles.p95 / 1000).toFixed(2))} suffix="s" prefix={<ThunderboltOutlined />} />
										</Col>
									</Row>

									<Card title="Response Time Distribution" style={{ marginBottom: 24 }}>
										<Row gutter={[16, 16]}>
											<Col span={12}>
												<div style={{ height: 300 }}>
													<ResponsiveContainer width="100%" height="100%">
														<BarChart data={[
															{ name: 'Fast (<2s)', value: responseTimeBins.fast || 0, color: '#52c41a' },
															{ name: 'Medium (2-5s)', value: responseTimeBins.medium || 0, color: '#faad14' },
															{ name: 'Slow (>5s)', value: responseTimeBins.slow || 0, color: '#ff4d4f' }
														]}>
															<CartesianGrid strokeDasharray="3 3" />
															<XAxis dataKey="name" />
															<YAxis />
															<Tooltip formatter={(value) => [value as number, 'Responses']} />
															<Bar dataKey="value">
																{[
																	{ name: 'Fast (<2s)', value: responseTimeBins.fast || 0, color: '#52c41a' },
																	{ name: 'Medium (2-5s)', value: responseTimeBins.medium || 0, color: '#faad14' },
																	{ name: 'Slow (>5s)', value: responseTimeBins.slow || 0, color: '#ff4d4f' }
																].map((entry, idx) => (
																	<Cell key={`cell-${idx}`} fill={entry.color} />
																))}
															</Bar>
														</BarChart>
													</ResponsiveContainer>
												</div>
											</Col>
											<Col span={12}>
												<div style={{ padding: '20px 0' }}>
													<h4>Performance Breakdown</h4>
													<div style={{ marginBottom: 16 }}>
														<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
															<span>Fast Responses (&lt; 2s)</span>
															<span>{responseTimeBins.fast || 0}</span>
														</div>
														<Progress percent={total > 0 ? Math.round(((responseTimeBins.fast || 0) / total) * 100) : 0} strokeColor="#52c41a" showInfo={false} />
													</div>
													<div style={{ marginBottom: 16 }}>
														<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
															<span>Medium Responses (2-5s)</span>
															<span>{responseTimeBins.medium || 0}</span>
														</div>
														<Progress percent={total > 0 ? Math.round(((responseTimeBins.medium || 0) / total) * 100) : 0} strokeColor="#faad14" showInfo={false} />
													</div>
													<div>
														<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
															<span>Slow Responses (&gt; 5s)</span>
															<span>{responseTimeBins.slow || 0}</span>
														</div>
														<Progress percent={total > 0 ? Math.round(((responseTimeBins.slow || 0) / total) * 100) : 0} strokeColor="#ff4d4f" showInfo={false} />
													</div>
												</div>
											</Col>
										</Row>
									</Card>

									<Card title="Hourly Response Time">
										<div style={{ height: 300 }}>
											<ResponsiveContainer width="100%" height="100%">
												<LineChart data={hourlyData}>
													<CartesianGrid strokeDasharray="3 3" />
													<XAxis dataKey="hour" />
													<YAxis label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft' }} />
													<Tooltip formatter={(value) => [`${value}ms`, 'Avg Response Time']} />
													<Line type="monotone" dataKey="avgResponseTime" stroke="#1890ff" strokeWidth={2} />
												</LineChart>
											</ResponsiveContainer>
										</div>
									</Card>

									{Array.isArray((data as any).trendData) && (data as any).trendData.length > 0 && (
										<Card title="Response Time Trend">
											<div style={{ height: 300 }}>
												<ResponsiveContainer width="100%" height="100%">
													<LineChart data={(data as any).trendData}>
														<CartesianGrid strokeDasharray="3 3" />
														<XAxis dataKey="date" tickFormatter={(value) => formatXAxisLabel(value, aggregation)} />
														<YAxis label={{ value: 'Response Time (ms)', angle: -90, position: 'insideLeft' }} />
														<Tooltip formatter={(value) => [`${value}ms`, 'Avg Response Time']} labelFormatter={(label) => formatTooltipLabel(label, aggregation)} />
														<Line type="monotone" dataKey="avgResponseTime" stroke="#1890ff" strokeWidth={2} />
													</LineChart>
												</ResponsiveContainer>
											</div>
										</Card>
									)}
								</>
							);
						})()}
					</>
				)}
			</div>
		);
	};

	return (
		<div style={{ height: '100%' }}>
			{isExpanded ? renderExpandedView() : renderCollapsedView()}
		</div>
	);
});

ResponseTimeAnalytics.displayName = 'ResponseTimeAnalytics';

export default ResponseTimeAnalytics;
